export const CALL_LABELS = ["chaud", "timing", "argent", "froid"] as const;
export type CallLabel = (typeof CALL_LABELS)[number];

export const CALL_LABEL_TEXT: Record<CallLabel, string> = {
  chaud: "Chaud",
  timing: "Timing",
  argent: "Pas d'argent",
  froid: "Pas intéressé",
};

export const CALL_LABEL_HINT: Record<CallLabel, string> = {
  chaud: "Vraiment prêt : il suffit de le relancer",
  timing: "Intéressé, mais pas sa priorité maintenant",
  argent: "A envie, mais pas les moyens : inutile d'insister",
  froid: "Pas envie, avec ou sans argent",
};

export type CallSource = "fireflies" | "fathom";

export interface SummaryInput {
  prospectId: number;
  source: CallSource;
  externalId: string;
  recordingUrl: string;
  callAt: string;
  label: CallLabel;
  phrase: string;
}

const MAX_WORDS = 12;
const HOUR = 3_600_000;

function isIso(s: string): boolean {
  return /^\d{4}-\d{2}-\d{2}T/.test(s) && !Number.isNaN(Date.parse(s));
}

const MAX_EXTERNAL_ID = 128;
const MAX_URL = 512;
const MAX_PHRASE = 200;
const ALLOWED_HOSTS = ["fireflies.ai", "fathom.video"];

/** https uniquement, hôte fireflies.ai / fathom.video ou sous-domaine. */
export function isAllowedRecordingUrl(raw: string): boolean {
  let u: URL;
  try {
    u = new URL(raw);
  } catch {
    return false;
  }
  if (u.protocol !== "https:") return false;
  const host = u.hostname.toLowerCase();
  return ALLOWED_HOSTS.some((h) => host === h || host.endsWith("." + h));
}

export function validateSummaryInput(
  x: unknown,
): { ok: true; value: SummaryInput } | { ok: false; error: string } {
  if (!x || typeof x !== "object") return { ok: false, error: "Corps invalide" };
  const o = x as Record<string, unknown>;
  const prospectId = o.prospectId;
  if (typeof prospectId !== "number" || !Number.isInteger(prospectId) || prospectId <= 0)
    return { ok: false, error: "prospectId invalide" };
  if (o.source !== "fireflies" && o.source !== "fathom")
    return { ok: false, error: "source invalide" };
  if (typeof o.externalId !== "string" || !o.externalId.trim())
    return { ok: false, error: "externalId manquant" };
  if (o.externalId.trim().length > MAX_EXTERNAL_ID)
    return { ok: false, error: "externalId trop long" };
  if (typeof o.recordingUrl !== "string" || !o.recordingUrl.trim())
    return { ok: false, error: "recordingUrl manquant" };
  if (o.recordingUrl.trim().length > MAX_URL)
    return { ok: false, error: "recordingUrl trop long" };
  if (!isAllowedRecordingUrl(o.recordingUrl.trim()))
    return { ok: false, error: "recordingUrl doit être en https sur fireflies.ai ou fathom.video" };
  if (typeof o.callAt !== "string" || !isIso(o.callAt))
    return { ok: false, error: "callAt doit être une date ISO" };
  if (typeof o.label !== "string" || !(CALL_LABELS as readonly string[]).includes(o.label))
    return { ok: false, error: "étiquette inconnue" };
  if (typeof o.phrase !== "string" || !o.phrase.trim())
    return { ok: false, error: "phrase vide" };
  const phrase = o.phrase.trim();
  if (phrase.length > MAX_PHRASE) return { ok: false, error: "phrase trop longue" };
  if (phrase.split(/\s+/).length > MAX_WORDS)
    return { ok: false, error: `phrase trop longue (max ${MAX_WORDS} mots)` };
  if (/:\/\/|www\.|http|\u2014/i.test(phrase))
    return { ok: false, error: "phrase invalide (pas d'URL ni de tiret cadratin)" };
  return {
    ok: true,
    value: {
      prospectId,
      source: o.source,
      externalId: o.externalId.trim(),
      recordingUrl: o.recordingUrl.trim(),
      callAt: new Date(o.callAt).toISOString(),
      label: o.label as CallLabel,
      phrase,
    },
  };
}

/** L'appel est rattaché à un RDV s'il tombe à ±12 h de son début. */
export function withinRdvWindow(callAtIso: string, rdvStartIsos: string[]): boolean {
  const t = Date.parse(callAtIso);
  return rdvStartIsos.some((r) => Math.abs(Date.parse(r) - t) <= 12 * HOUR);
}

export function decideUpsert(
  existing: { id: number; source: CallSource; callAt: string }[],
  incoming: { source: CallSource; callAt: string },
): { action: "insert" } | { action: "skip" } | { action: "replace"; ids: number[] } {
  const t = Date.parse(incoming.callAt);
  const near = existing.filter(
    (e) => e.source !== incoming.source && Math.abs(Date.parse(e.callAt) - t) <= 3 * HOUR,
  );
  if (incoming.source === "fathom" && near.some((e) => e.source === "fireflies"))
    return { action: "skip" };
  if (incoming.source === "fireflies") {
    const ids = near.filter((e) => e.source === "fathom").map((e) => e.id);
    if (ids.length > 0) return { action: "replace", ids };
  }
  return { action: "insert" };
}

export function latestSummary<T extends { callAt: string }>(rows: T[]): T | null {
  let best: T | null = null;
  for (const r of rows) if (!best || Date.parse(r.callAt) > Date.parse(best.callAt)) best = r;
  return best;
}
